import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const resolveApiUrl = () => {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  const isBrowser = typeof window !== 'undefined';
  const origin = isBrowser ? window.location.origin : '';
  const normalized = raw.replace(/\/$/, '');

  // In production behind Nginx, local loopback URLs break client-side requests.
  const isLocalLoopback = /^(https?:\/\/)?(127\.0\.0\.1|localhost)(:\d+)?$/i.test(normalized);
  const base = !normalized || (process.env.NODE_ENV === 'production' && isLocalLoopback)
    ? origin
    : normalized;

  return /\/api$/i.test(base) ? base : `${base}/api`;
};

const API_URL = resolveApiUrl();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedTheme = localStorage.getItem('preferred_theme');
    if (storedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (storedTheme === 'light') {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Set axios default header whenever token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch user on mount if token exists
  useEffect(() => {
    const initAuth = async () => {
      const storedLanguage = localStorage.getItem('tontine_language');
      if (storedLanguage && ['fr', 'en', 'wo'].includes(storedLanguage)) {
        axios.defaults.headers.common['Accept-Language'] = storedLanguage;
      }

      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        await fetchUser();
      } else {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  useEffect(() => {
    if (typeof user?.preferences?.dark_mode !== 'boolean') {
      return;
    }

    const isDarkMode = user.preferences.dark_mode;
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.style.colorScheme = isDarkMode ? 'dark' : 'light';
    localStorage.setItem('preferred_theme', isDarkMode ? 'dark' : 'light');
  }, [user?.preferences?.dark_mode]);

  useEffect(() => {
    const preferredLanguage = user?.preferences?.preferred_language;
    if (!preferredLanguage || !['fr', 'en', 'wo'].includes(preferredLanguage)) {
      return;
    }

    localStorage.setItem('tontine_language', preferredLanguage);
    axios.defaults.headers.common['Accept-Language'] = preferredLanguage;
    window.dispatchEvent(new CustomEvent('tontine-language-sync', {
      detail: { language: preferredLanguage }
    }));
  }, [user?.preferences?.preferred_language]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      // Only logout if it's an auth error, not a network error
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API_URL}/auth/me`, data);
    setUser(response.data);
    return response.data;
  };

  const updatePreferences = async (data) => {
    const response = await axios.put(`${API_URL}/auth/preferences`, data);
    setUser(response.data);
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser, updateProfile, updatePreferences }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
